import ExcelJS from 'exceljs';

const SHEET_NAME = 'AdoptionCurve';
const COL_YEAR = 'Year';
const COL_ADOPTION = 'Adoption (%)';

/**
 * Download an XLSX template prefilled with the current baseline curve.
 * @param {{ startYear: number, endYear: number, baselinePoints: Array<{x:number,y:number}>, utilityName?: string }} opts
 */
export async function downloadAdoptionCurveTemplate({
  startYear,
  endYear,
  baselinePoints,
  utilityName,
}) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'GridPlan';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet(SHEET_NAME, {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  sheet.columns = [
    { header: COL_YEAR, key: 'year', width: 12 },
    { header: COL_ADOPTION, key: 'adoption', width: 14 },
  ];
  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  };

  const points = Array.isArray(baselinePoints) ? baselinePoints : [];
  points.forEach((pt) => {
    sheet.addRow({ year: pt.x, adoption: typeof pt.y === 'number' ? pt.y : 0 });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const safeName = (utilityName || 'adoption_curve').replace(/[^\w\-]/g, '_');
  link.download = `EV_adoption_curve_${safeName}.xlsx`;
  link.click();
  window.URL.revokeObjectURL(url);
}

/**
 * Parse an uploaded XLSX and return baseline points. Validates columns and values;
 * fills missing years by linear interpolation; ensures start/end years exist.
 * @param {File} file
 * @param {number} startYear
 * @param {number} endYear
 * @returns {Promise<{ points: Array<{x:number,y:number}> }>}
 */
export async function parseAdoptionCurveXlsx(file, startYear, endYear) {
  const workbook = new ExcelJS.Workbook();
  const arrayBuffer = await file.arrayBuffer();
  await workbook.xlsx.load(arrayBuffer);

  const sheet =
    workbook.getWorksheet(SHEET_NAME) ||
    workbook.worksheets.find(
      (ws) => ws && ws.name && ws.name.toString().trim().toLowerCase() === SHEET_NAME.toLowerCase()
    ) ||
    workbook.worksheets[0];

  if (!sheet) {
    throw new Error('No worksheet found. Expected sheet name "AdoptionCurve".');
  }

  const headerRow = sheet.getRow(1);
  const colIndex = {};
  headerRow.eachCell((cell, colNumber) => {
    const val = cell.value?.toString().trim();
    if (val) colIndex[val] = colNumber;
  });

  const yearCol = colIndex[COL_YEAR] ?? colIndex['Year'];
  const adoptionCol = colIndex[COL_ADOPTION] ?? colIndex['Adoption (%)'] ?? colIndex['Adoption'];
  if (!yearCol || !adoptionCol) {
    throw new Error(
      `Missing required columns: "${COL_YEAR}" and "${COL_ADOPTION}". Please use the downloaded template.`
    );
  }

  const rawPoints = [];
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    const yearVal = row.getCell(yearCol).value;
    const adoptionVal = row.getCell(adoptionCol).value;
    if (yearVal == null || adoptionVal == null) return;
    const year = Number(yearVal);
    const adoption = Number(adoptionVal);
    if (isNaN(year) || isNaN(adoption)) return;
    if (year < startYear || year > endYear) {
      throw new Error(
        `Row ${rowNumber}: Year ${year} is outside forecast range [${startYear}, ${endYear}].`
      );
    }
    if (adoption < 0 || adoption > 100) {
      throw new Error(`Row ${rowNumber}: Adoption ${adoption} must be between 0 and 100.`);
    }
    rawPoints.push({ x: year, y: Number(adoption.toFixed(2)) });
  });

  if (rawPoints.length === 0) {
    throw new Error('No valid data rows found. Ensure Year and Adoption (%) are filled.');
  }

  rawPoints.sort((a, b) => a.x - b.x);

  const byYear = new Map(rawPoints.map((p) => [p.x, p.y]));
  const allYears = [];
  for (let y = startYear; y <= endYear; y++) allYears.push(y);

  const points = allYears.map((year) => {
    if (byYear.has(year)) {
      return { x: year, y: byYear.get(year) };
    }
    const before = rawPoints.filter((p) => p.x < year);
    const after = rawPoints.filter((p) => p.x > year);
    const prev = before.length ? before[before.length - 1] : null;
    const next = after.length ? after[0] : null;
    if (!prev && !next) return { x: year, y: 0 };
    if (!prev) return { x: year, y: next.y };
    if (!next) return { x: year, y: prev.y };
    const t = (year - prev.x) / (next.x - prev.x);
    const y = prev.y + t * (next.y - prev.y);
    return { x: year, y: Math.min(100, Math.max(0, Number(y.toFixed(2)))) };
  });

  return { points };
}
